import { createSelector } from "@reduxjs/toolkit";

/* -------------------------------------------------------------------------- */
/* 🧩 Base Selector                                                           */
/* -------------------------------------------------------------------------- */
export const selectModerationState = (state) => state.moderation || {};

/* -------------------------------------------------------------------------- */
/* 🗂️ Posts Selectors                                                         */
/* -------------------------------------------------------------------------- */
export const selectModerationPosts = createSelector(
  [selectModerationState],
  (moderation) => {
    const posts = moderation.posts || [];
    return posts;
  }
);

/** Post sélectionné (pour affichage détaillé) */
export const selectModerationSelectedPost = createSelector(
  [selectModerationState],
  (moderation) => moderation.selectedPost
);

/** Nombre total de posts */
export const selectModerationPostsCount = createSelector(
  [selectModerationPosts],
  (posts) => posts.length
);

/** Vérifie si au moins un post existe */
export const selectHasPosts = createSelector(
  [selectModerationPosts],
  (posts) => posts.length > 0
);

/** Posts filtrés pour un utilisateur spécifique (utile si on garde tout en store) */
export const selectPostsByUserId = (userId) =>
  createSelector([selectModerationPosts], (posts) =>
    posts.filter((post) => post.user_id === userId)
  );

/* -------------------------------------------------------------------------- */
/* ⏳ Loading Selectors                                                       */
/* -------------------------------------------------------------------------- */
export const selectModerationLoading = createSelector(
  [selectModerationState],
  (moderation) => moderation.loading?.posts || false
);

export const selectModerationCreateLoading = createSelector(
  [selectModerationState],
  (moderation) => moderation.loading?.create || false
);

export const selectModerationStatsLoading = createSelector(
  [selectModerationState],
  (moderation) => moderation.loading?.stats || false
);

/** Regroupe tous les chargements (utile pour les loaders globaux) */
export const selectIsLoadingAnyModeration = createSelector(
  [selectModerationState],
  (moderation) => {
    const loading = moderation.loading || {};
    return loading.posts || loading.create || loading.stats;
  }
);

/* -------------------------------------------------------------------------- */
/* ⚠️ Error Selectors                                                        */
/* -------------------------------------------------------------------------- */
export const selectModerationError = createSelector(
  [selectModerationState],
  (moderation) => moderation.error
);

export const selectModerationErrorMessage = createSelector(
  [selectModerationState],
  (moderation) => moderation.errorMessage
);

/* -------------------------------------------------------------------------- */
/* 📊 Stats & Pagination Selectors                                           */
/* -------------------------------------------------------------------------- */
export const selectModerationStats = createSelector(
  [selectModerationState],
  (moderation) => moderation.stats || {}
);

export const selectModerationPagination = createSelector(
  [selectModerationState],
  (moderation) =>
    moderation.pagination || {

      hasMore: true,
    }
);

export const selectMedicalQuestions = createSelector(
  [selectModerationPosts],
  (posts) => {
    return posts.filter((post) => {
      // Filtrer les posts bloqués
      if (post.blocked === true) return false;

      // Si vous avez un champ "type" ou "category", filtrer par type
      // if (post.type !== "medical_question") return false;

      // Si vous avez un statut de modération, filtrer par statut
      // if (post.moderation_status === "rejected") return false;

      return true;
    });
  }
);

export const selectMedicalQuestionsStats = createSelector(
  [selectMedicalQuestions],
  (questions) => {
    const total = questions.length;
    const withPhotos = questions.filter((q) => q.photo).length;
    const recentQuestions = questions.filter((q) => {
      const postDate = new Date(q.date);
      const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return postDate > dayAgo;
    }).length;

    return {
      total,
      withPhotos,
      recentQuestions,
      withoutPhotos: total - withPhotos,
    };
  }
);
export const selectMedicalQuestionsByStatus = (status) =>
  createSelector([selectMedicalQuestions], (questions) =>
    questions.filter((q) => q.moderation_status === status)
  );

/**
 * 🕐 Trier les questions médicales par date (plus récentes en premier)
 */
export const selectMedicalQuestionsSortedByDate = createSelector(
  [selectMedicalQuestions],
  (questions) => {
    return [...questions].sort((a, b) => {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return dateB - dateA; // Plus récentes en premier
    });
  }
);

export const selectFilteredMedicalQuestions = (searchTerm) =>
  createSelector([selectMedicalQuestions], (questions) => {
    if (!searchTerm || searchTerm.trim() === "") return questions;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return questions.filter((q) => {
      const description = (q.description || "").toLowerCase();
      const userName = `${q.user?.firstName || ""} ${
        q.user?.lastName || ""
      }`.toLowerCase();

      return (
        description.includes(lowerSearchTerm) ||
        userName.includes(lowerSearchTerm)
      );
    });
  });
